/*
 * tc-events.js — commerce-event capture + anonymous-id cart stamping.
 *
 * Two jobs, one deferred script (loads after pubsub.js; uses window.TC_TRACKING
 * from the tc-tracking-config snippet and window.routes from theme.liquid):
 *
 * 1) add_to_cart → collector.
 *    The THEME owns add_to_cart, not our web pixel. The Koala buy box
 *    (flash-forward.js) does a bulk /cart/add.js + instant redirect that tears
 *    down the pixel sandbox before Shopify's native product_added_to_cart can
 *    relay (see docs/TRACKING_DATALAYER.md) — a pixel-based capture would
 *    systematically miss the store's primary add-to-cart path and bias every
 *    experiment metric built on it. Instead we observe window.dataLayer, where
 *    add_to_cart_stape reliably lands for EVERY path (theme-pushed for the
 *    bundler via tinCanPushAddToCart, Stape-pixel-relayed for native adds),
 *    and apply the same variant-signature+4s dedup GTM uses for the
 *    occasional theme/native double-fire.
 *
 * 2) tc_anonymous_id cart attribute.
 *    Stamped via /cart/update.js so it lands on the order's note_attributes —
 *    the pixel-independent join between experiments and the authoritative
 *    order record (orders webhook, admin "Additional details").
 */
(function () {
  var cfg = window.TC_TRACKING;
  if (!cfg || !cfg.enabled) return;

  /* ------------------------------------------------------------------ *
   * 1) add_to_cart capture via dataLayer                                *
   * ------------------------------------------------------------------ */

  // Same signature rule as the GTM dedup variable (docs/TRACKING_DATALAYER.md):
  // identical item set within 4s = the theme/native double-fire, not a real add.
  var lastAtc = { sig: null, t: 0 };
  function isDuplicateAdd(items) {
    var sig = items
      .map(function (i) { return i.item_variant + 'x' + (i.quantity || 1); })
      .sort()
      .join('|');
    var now = Date.now();
    var dup = lastAtc.sig === sig && now - lastAtc.t < 4000;
    lastAtc = { sig: sig, t: now };
    return dup;
  }

  function onDataLayerEvent(entry) {
    try {
      if (!entry || entry.event !== 'add_to_cart_stape') return;
      var ec = entry.ecommerce;
      if (!ec || !ec.items || !ec.items.length) return;
      if (isDuplicateAdd(ec.items)) return;
      cfg.send({
        type: 'track',
        event_name: 'product_added_to_cart',
        // Theme-pushed events carry an event_id; reuse it so this row can be
        // correlated with the GTM/Stape copy of the same add.
        event_id: entry.event_id || undefined,
        anonymous_id: cfg.anonymousId,
        timestamp: new Date().toISOString(),
        environment: cfg.environment,
        source: 'shopify_theme',
        properties: {
          value: ec.value,
          currency: ec.currency,
          items: ec.items.map(function (i) {
            return {
              item_id: i.item_id,
              item_variant: i.item_variant,
              quantity: i.quantity,
              price: i.price
            };
          }),
          url: location.href
        }
      });
      stampCartAttribute();
    } catch (e) { /* never break other dataLayer consumers */ }
  }

  // Read-only observer: wrap push, always delegate first. GTM wraps push the
  // same way; wrapping the current reference keeps the whole chain intact.
  var dl = (window.dataLayer = window.dataLayer || []);
  var originalPush = dl.push;
  dl.push = function () {
    var result = originalPush.apply(dl, arguments);
    for (var i = 0; i < arguments.length; i++) onDataLayerEvent(arguments[i]);
    return result;
  };

  /* ------------------------------------------------------------------ *
   * 2) cart attribute stamping                                          *
   * ------------------------------------------------------------------ */

  var stamping = false;
  function stampCartAttribute() {
    if (!window.routes || !window.routes.cart_url) return;
    if (stamping) return;
    var guard = null;
    try { guard = sessionStorage.getItem('tc_cart_stamped'); } catch (e) { /* storage blocked */ }
    if (guard === cfg.anonymousId) return;
    stamping = true;

    // Read first: only write when there is a cart and the attribute is
    // missing/stale. /cart/update.js merges attribute keys, so this never
    // clobbers attributes other scripts set.
    fetch(window.routes.cart_url + '.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !cart.token) return;
        if (cart.attributes && cart.attributes.tc_anonymous_id === cfg.anonymousId) {
          try { sessionStorage.setItem('tc_cart_stamped', cfg.anonymousId); } catch (e) { /* ignore */ }
          return;
        }
        if (cart.item_count === 0) return; // nothing to attribute yet
        return fetch(window.routes.cart_update_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ attributes: { tc_anonymous_id: cfg.anonymousId } })
        }).then(function (r) {
          if (r.ok) {
            try { sessionStorage.setItem('tc_cart_stamped', cfg.anonymousId); } catch (e) { /* ignore */ }
          }
        });
      })
      .catch(function () {})
      .finally(function () { stamping = false; });
  }

  // Dawn paths (product-form add, cart drawer quantity changes) publish
  // cartUpdate on pubsub. The buy box's instant redirect never reaches this —
  // covered by the on-load stamp on the /cart page it redirects to.
  if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () { stampCartAttribute(); });
  }
  stampCartAttribute();
})();
